import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  ViewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { Memo, User, Comment } from 'shared/models';
import { AlertService, DialogService, ToastService } from 'shared/ui';
import { takeUntil } from 'rxjs';

import {
  APIError,
  CommentAPIService,
  SharedUserService,
} from 'shared/services';
import { EventBusService } from '../../services/event-bus.service';
import { AnnotationsV2Service } from '../../services/annotations-v2.service';
import { ReadingGroupsService } from '../../services/reading-groups.service';
import { BookService } from '../../services/book.service';
import { parseBukURL } from '@bukio/viewer';

import { MemoType } from '../memo/memo.component';
import { MemoExportDialogComponent } from '../../dialogs/memo-export-dialog/memo-export-dialog.component';
import { MemoDialogComponent } from '../../dialogs/memo-dialog/memo-dialog.component';
import { MemoCopyDialogComponent } from '../../dialogs/memo-copy-dialog/memo-copy-dialog.component';
import { MemoReportDialogComponent } from '../../dialogs/memo-report-dialog/memo-report-dialog.component';
import { UserDetailsDialogComponent } from '../../dialogs/user-details-dialog/user-details-dialog.component';
import { CommentatorDetailsDialogComponent } from '../../dialogs/commentator-details-dialog/commentator-details-dialog.component';
import { CommentReportDialogComponent } from '../../dialogs/comment-report-dialog/comment-report-dialog.component';
import { Book } from '../../models/book.model';

@Component({
  selector: 'viewer-reading-mode-memo',
  templateUrl: './reading-mode-memo.component.html',
  styleUrl: './reading-mode-memo.component.scss',
})
export class ReadingModeMemoComponent implements OnInit, OnDestroy {
  @ViewChild('replyInput') replyInput!: ElementRef<HTMLInputElement>;
  @ViewChild('nicknameElement') nicknameElement!: ElementRef<HTMLInputElement>;

  @Output() memoDeleted = new EventEmitter<void>();
  @Output() memoBlinded = new EventEmitter<void>();
  @Output() memoUserBlocked = new EventEmitter<void>();
  @Output() memoSeeDetailsButtonClicked = new EventEmitter<void>();
  @Output() memoPinOptionChanged = new EventEmitter<void>();

  @Output() memoUpdated = new EventEmitter<Partial<Memo>>();
  @Output() replyCommentClick = new EventEmitter<{
    memo: Memo;
    comment: Memo;
    commentState: 'comment' | 'subComment' | 'subSubComment';
    reply?: Memo;
  }>();
  @Output() subReplyCommentChanged = new EventEmitter<{
    memo: Memo;
    reply: Comment;
  }>();

  // 추가: 현재 메모 활성화 여부
  public _isActive = false;

  @Input() text!: string;
  @Input() limitHeight = false;
  @Input() memo!: Memo;
  @Input() isAuthorOnline?: boolean;
  @Input() isUnread?: boolean;
  @Input() isSmallPopup = false;
  @Input() isReplyCommentOpen = false; // 답글 목록 열림 여부
  @Input() currentPage?: number;
  @Input() isOwnedByUser = false;

  public _currentUser?: User;
  public _bid: string;
  private _groupId?: number;
  private _commentaryId?: number;
  public _canShareItem = false;

  private static readonly _likeDebounceMs = 300;

  /** 메모 추천: 디바운스 구간 시작 시점의 서버 반영 상태(실패 시 되돌리기) */
  private _memoLikeBaseline: { like_count: number; is_liked: number } | null =
    null;
  private _memoLikeIntended: { like_count: number; is_liked: number } | null =
    null;
  private _memoLikeDebounceHandle: ReturnType<typeof setTimeout> | null = null;

  /** 답글/대댓글 추천: 키별 디바운스 및 되돌리기용 베이스라인 */
  private _replyLikeBaseline = new Map<
    string,
    { like_count: number; is_liked: number }
  >();
  private _replyLikeDebounceHandles = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  private _isCreatingMemo = false; // 답글 생성/수정 중 플래그 추가
  private _unsubscriber = new Subject<void>();

  public _isSingleMode = false;
  public _isRequestReplyComment = false;

  public _replyCommentInputValue = ''; // 답글 입력값
  public _subReplyCommentInputValue = ''; // 답글의 답글 입력값
  public _subReplyCommentInputValues: { [commentId: string]: string } = {}; // 답글의 답글 입력값을 개별 관리
  public _subSubReplyCommentInputValues: { [replyId: string]: string } = {}; // 답글의 답글의 답글 입력값을 개별 관리
  public _replyCommentShortContents: {
    [commentId: string]: string | undefined;
  } = {}; // 답글의 짧은 내용을 개별 관리
  public _subReplyShortContents: {
    [replyId: string]: string | undefined;
  } = {}; // 다답글의 짧은 내용을 개별 관리
  public _replyComments: any[] = [];
  public _isReplyCommentInputOpen = false; // 답글 입력 박스 열림 여부
  public _isSubReplyInputOpen = false; // 답글의 답글 입력 박스 열림 여부
  public _isReplyCommentListOpen = false; // 답글 목록 열림 여부
  public _isReplyCommentListLoading = false; // 답글 목록 로딩 여부
  public _isReplyCommentListEmpty = false; // 답글 목록을 모두 지웠는지 여부

  public _selectedCommentForMoreButton: Comment | null = null; // 더보기 버튼에서 선택된 댓글 (_selectedCommentForMenu에 넣기위한 임시 데이터 저장소)

  constructor(
    bookService: BookService,
    private _alertService: AlertService,
    private _annotationsService: AnnotationsV2Service,
    private _eventBusService: EventBusService,
    private _readingGroupsService: ReadingGroupsService,
    private _toastService: ToastService,
    private _dialogService: DialogService,
    private _commentApiService: CommentAPIService,
    private _userService: SharedUserService,
    private _bookService: BookService,
    private _router: Router
  ) {
    if (bookService.book) {
      this._bid = bookService.book.meta.bid;
    } else {
      throw new Error('book is null');
    }

    this._groupId = this._annotationsService.getState().groupId;
    this._commentaryId = this._annotationsService.getState().commentaryId;

    this._isSingleMode = this._groupId == null;
  }

  ngOnInit(): void {
    this._bookService.book$
      .pipe(
        filter((book): book is Book => book !== null),
        takeUntil(this._unsubscriber)
      )
      .subscribe((book) => {
        if (this.memo?.url) {
          const address = parseBukURL(this.memo.url);
          const rangeIID = address.iid || '';
          this._canShareItem = book.canShareItem(rangeIID);
        }
      });

    this._userService.user
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((user) => {
        if (user) {
          this._currentUser = user;
        }
      });

    this._eventBusService
      .on('MemoListComponent:memoDeleted')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        if (data.memoId === this.memo.id) {
          this.memoDeleted.emit();
        }
      });

    this._eventBusService
      .on('MemoListComponent:memoUpdated')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data) => {
        if (data) {
          this.memoUpdated.emit({
            like_count: data.updates.like_count,
            is_liked: data.updates.is_liked,
            copy_count: data.updates.copy_count,
          });
        }
      });

    // 추가: 본문 보기 클릭 이벤트 구독 (다른 메모가 클릭되면 내 활성화 상태 끄기 위함)
    this._eventBusService
      .on('ReadingModeMemoComponent:seeDetailsButtonClick')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data: any) => {
        // 클릭된 메모 ID가 내 ID와 같으면 활성화, 아니면 비활성화
        this._isActive = data.id === this.memo.id;
      });

    // 메모로 스크롤 이동 이벤트 구독
    this._eventBusService
      .on('CommentRightPanelComponent:scrollToMemo')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(({ memoId, source }) => {
        if (source === 'reading-mode') {
          if (memoId === this.memo.id) {
            this._scrollToThisMemo();
          }
        }
      });

    // 답글 추가 이벤트 구독
    this._eventBusService
      .on('ReadingGroupMemoListComponent:replyCommentAdded')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data: { memoId: string; comment: any }) => {
        // 현재 메모의 답글이면 추가
        if (data.memoId === this.memo.id) {
          // 답글 더보기가 닫혀있으면 열기
          if (!this._isReplyCommentListOpen) {
            this._isReplyCommentListOpen = true;

            // 답글 목록이 아직 로드되지 않았다면 로드
            if (!this._isRequestReplyComment) {
              this._isRequestReplyComment = true;

              this._commentApiService
                .getComments(this.memo.id)
                .subscribe((result) => {
                  if (result) {
                    this._replyComments = result.items.map((item) => {
                      item.isSubReplyOpened = 0;
                      item.isSubReplyChangeOpened = 0;
                      // 답글의 짧은 내용 계산
                      this._replyCommentShortContents[item.id] =
                        this._calculateShortContent(item.content);

                      item.replies = item.replies.map((reply) => {
                        reply.isSubReplyOpened = 0;
                        reply.isSubReplyChangeOpened = 0;
                        // 다답글의 짧은 내용 계산
                        this._subReplyShortContents[reply.id] =
                          this._calculateShortContent(reply.content);

                        return reply;
                      });

                      return item;
                    });
                  }
                });
            } else {
              // 이미 로드된 답글 목록이 있다면 새 답글 추가
              data.comment.isSubReplyOpened = 0;
              data.comment.isSubReplyChangeOpened = 0;
              // 답글의 짧은 내용 계산
              this._replyCommentShortContents[data.comment.id] =
                this._calculateShortContent(data.comment.content);
              // 답글의 답글들도 처리
              if (data.comment.replies) {
                data.comment.replies = data.comment.replies.map(
                  (reply: any) => {
                    reply.isSubReplyOpened = 0;
                    reply.isSubReplyChangeOpened = 0;
                    // 다답글의 짧은 내용 계산
                    this._subReplyShortContents[reply.id] =
                      this._calculateShortContent(reply.content);
                    return reply;
                  }
                );
              }
              this._replyComments.push(data.comment);
            }
          } else {
            // 답글 목록이 비어있으면 초기화
            if (this._replyComments.length === 0) {
              this._replyComments = [];
            }

            // 답글 추가
            data.comment.isSubReplyOpened = 0;
            data.comment.isSubReplyChangeOpened = 0;
            // 답글의 짧은 내용 계산
            this._replyCommentShortContents[data.comment.id] =
              this._calculateShortContent(data.comment.content);
            // 답글의 답글들도 처리
            if (data.comment.replies) {
              data.comment.replies = data.comment.replies.map((reply: any) => {
                reply.isSubReplyOpened = 0;
                reply.isSubReplyChangeOpened = 0;
                // 다답글의 짧은 내용 계산
                this._subReplyShortContents[reply.id] =
                  this._calculateShortContent(reply.content);
                return reply;
              });
            }
            this._replyComments.push(data.comment);
          }

          this.memo.comment_count++;
        }
      });

    // 답글의 답글 추가 이벤트 구독
    this._eventBusService
      .on('ReadingGroupMemoListComponent:subReplyCommentAdded')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((data: { memoId: string; commentId: string; reply: any }) => {
        if (data.memoId === this.memo.id) {
          const comment = this._replyComments.find(
            (comment) => comment.id === data.commentId
          );

          if (comment) {
            data.reply.isSubReplyOpened = 0;
            data.reply.isSubReplyChangeOpened = 0;
            // 다답글의 짧은 내용 계산
            this._subReplyShortContents[data.reply.id] =
              this._calculateShortContent(data.reply.content);
            comment.replies.push(data.reply);
            this.memo.comment_count++;
            comment.isSubReplyOpened = 0;
          }
        }
      });

    // 답글의 답글의 답글 추가 이벤트 구독
    this._eventBusService
      .on('ReadingGroupMemoListComponent:subSubReplyCommentAdded')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(
        (data: {
          memoId: string;
          commentId: string;
          replyId: string;
          reply: any;
        }) => {
          this._subSubReplyCommentInputValues[data.replyId] = '';

          if (data.memoId === this.memo.id) {
            const comment = this._replyComments.find(
              (comment) => comment.id === data.commentId
            );

            if (comment) {
              comment?.replies.push(data.reply);

              const reply = comment.replies.find(
                (reply: any) => reply.id === data.replyId
              );

              if (reply) {
                reply.replies = reply.replies || [];
                reply.replies.push(data.reply);
                reply.isSubReplyOpened = 0;
                this.memo.comment_count++;
              }
            }
          }
        }
      );

    // 답글 수정 이벤트 구독
    this._eventBusService
      .on('ReadingGroupMemoListComponent:subReplyCommentChanged')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(
        (data: { memo: Memo; originalReply: Comment; reply: Comment }) => {
          data.originalReply.content = data.reply.content;
          data.originalReply.updated_at = data.reply.updated_at;
          data.originalReply.isSubReplyChangeOpened = 0;
        }
      );

    // 페이지 전환 시 댓글 포커스 해제 (사용자가 직접 페이지 전환한 경우만)
    this._eventBusService
      .on('ContentsComponent:pageInfoChange')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe((result) => {
        // anchor가 숫자가 아니면 댓글 포커스 해제 (의도적으로 페이지 전환하는 경우)
        // epub의 경우 의도적으로 페이지 전환시 number 타입 숫자가 설정됨
        if (
          !result.address.anchor ||
          typeof result.address.anchor === 'number'
        ) {
          this._isActive = false;
        }
      });

    // 드래그할때 본문 보기 비활성화 이벤트 구독
    this._eventBusService
      .on('ReadingModeMemoComponent:cancelViewText')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._isActive = false;
      });

    // 패널 닫힐 때 본문 보기 비활성화
    this._eventBusService
      .on('RightPanelComponent:closed')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        this._isActive = false;
      });
  }

  ngOnDestroy(): void {
    // textarea 스크롤 타이머 정리
    this._textareaScrollTimeouts.forEach((timeout) => clearTimeout(timeout));
    this._textareaScrollTimeouts.clear();

    this._resetMemoLikePendingState();
    this._replyLikeDebounceHandles.forEach((h) => clearTimeout(h));
    this._replyLikeDebounceHandles.clear();
    this._replyLikeBaseline.clear();

    this._unsubscriber.next();
    this._unsubscriber.complete();
  }

  get _singleMode(): boolean {
    return this._groupId == null;
  }

  get type(): MemoType {
    if (this.memo.is_mine) {
      return 'mine';
    }

    return this._singleMode ? 'commentary' : 'group';
  }

  // 현재 메모가 내 메모인지 판단하는 로직
  get _isMyMemo(): boolean {
    if (!this._currentUser || !this.memo?.author) {
      return false;
    }
    // User.id는 number, memo.author.id는 string이므로 타입 변환 필요
    return String(this._currentUser.id) === this.memo.author.id;
  }

  _onSeeDetailsButtonClick(): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    // 이미 활성화되어 있으면 취소
    if (this._isActive) {
      this._isActive = false;
      // 하이라이트 제거를 위한 이벤트 발생
      this._eventBusService.fire(
        'ReadingModeMemoComponent:seeDetailsButtonCancel',
        {
          url: this.memo.url,
          id: this.memo.id,
          source: 'viewTextCancel', // 본문보기 해제를 구분하기 위한 source 추가
        }
      );
      return;
    }

    // 본문 보기 활성화
    this._isActive = true;
    this.memoSeeDetailsButtonClicked.emit();

    this._eventBusService.fire(
      'ReadingModeMemoComponent:seeDetailsButtonClick',
      {
        url: this.memo.url,
        id: this.memo.id, // 식별을 위해 id 추가
        source: 'viewText', // 소스 정보 추가
      }
    );
  }

  _onEditMenuClick(): void {
    const wasPinned = this.memo.pinned_at != null;

    this._dialogService
      .open(MemoDialogComponent, {
        data: {
          data: {
            id: this.memo.id,
            text: this.text,
            content: this.memo.content,
            pinned: wasPinned,
          },
          type: 'commentEdit',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.deleted) {
          this.memoDeleted.emit();
        } else if (result?.updated) {
          this.memoUpdated.emit({
            content: result.updated.content,
            updated_at: new Date().toISOString(),
          });

          if (wasPinned !== result.updated.pinned) {
            this.memoPinOptionChanged.emit();
          }
          // this.memo.content = result.updatedMemo;
          // this._content = result.updatedMemo;
        }
      });
  }

  _onDeleteMenuClick(): void {
    this._annotationsService.deleteMemosById(this.memo.id).subscribe({
      next: () => {
        this._toastService.open('댓글이 삭제되었습니다.');
        this.memoDeleted.emit();

        // 현재 활성화 되어있는 댓글을 삭제하는 경우 하이라이트삭제 및 주소 변환
        if (this._isActive) {
          this._eventBusService.fire(
            'ReadingModeMemoComponent:seeDetailsButtonCancel',
            {
              url: this.memo.url,
              id: this.memo.id,
              source: 'viewTextCancel', // 본문보기 해제를 구분하기 위한 source 추가
            }
          );
        }
      },
      error: (error: APIError) => {
        this._toastService.openWarning(error.message);
      },
    });
  }

  _onExportMenuClick(): void {
    if (this._readingGroupsService.myGroups.length === 0) {
      this._toastService.openWarning('메모 내보내기가 가능한 그룹이 없습니다.');
    } else {
      this._dialogService.open(MemoExportDialogComponent, {
        data: {
          id: this.memo.id,
          content: this.memo.content,
        },
      });
    }
  }

  _onReportMenuClick(): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    this._dialogService
      .open(MemoReportDialogComponent, {
        data: {
          authorName: this.memo.author.nickname,
          authorAvatarImageURL: this.memo.author.avatar_image_url,
          memoId: this.memo.id,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.blocked) {
          this.memoUserBlocked.emit();
        }
      });
  }

  _onBlindMenuClick(): void {
    this._alertService
      .openConfirm(
        undefined,
        '메모를 숨김 처리합니다. 숨김 처리 후에는 다시 볼 수 없습니다. 계속하시겠습니까?',
        '계속하기'
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this._annotationsService.blockMemo(this.memo.id).subscribe(() => {
            this.memoBlinded.emit();
          });
        }
      });
  }

  // 댓글 추천 이벤트
  _onLikeButtonClick(): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    if (this._memoLikeBaseline === null) {
      this._memoLikeBaseline = {
        like_count: this.memo.like_count,
        is_liked: this.memo.is_liked,
      };
    }

    const prev =
      this._memoLikeIntended !== null
        ? this._memoLikeIntended
        : this._memoLikeBaseline;
    this._memoLikeIntended = {
      is_liked: prev.is_liked === 1 ? 0 : 1,
      like_count: prev.like_count + (prev.is_liked === 1 ? -1 : 1),
    };
    this.memoUpdated.emit(this._memoLikeIntended);
    this._scheduleMemoLikeDebounce();
  }

  // 댓글 복사 아이콘 클릭 이벤트
  _onCopyToSingleModeButtonClick(): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    this._dialogService
      .open(MemoCopyDialogComponent, {
        data: {
          text: this.text,
          content: this.memo.content,
          author: this.memo.author.nickname,
          memoId: this.memo.id,
        },
      })
      .afterClosed()
      .subscribe((copied) => {
        if (copied) {
          this.memoUpdated.emit({
            copy_count: this.memo.copy_count + 1,
          });
        }
        // this._copyCount++;
      });
  }

  _onAuthorClick(): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    if (this._commentaryId != null) {
      this._dialogService.open(CommentatorDetailsDialogComponent, {
        data: {
          bid: this._bid,
          avatarImageURL: this.memo.author.avatar_image_url,
          nickname: this.memo.author.nickname,
          commentaryId: this._commentaryId,
        },
      });
    } else if (this._groupId != null) {
      this._dialogService
        .open(UserDetailsDialogComponent, {
          data: {
            bid: this._bid,
            userId: this.memo.author.id,
            isUserBlocked: false,
            groupId: this._groupId,
          },
        })
        .afterClosed()
        .subscribe((result) => {
          if (result?.userBlocked) {
            this._eventBusService.fire('UserBlocked:refreshMemoLists');
          }
        });
    }
  }

  _onCommentAuthorClick(author: {
    bid: string;
    id: string;
    avatar_image_url: string;
    nickname: string;
  }): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    if (this._groupId != null) {
      this._dialogService
        .open(UserDetailsDialogComponent, {
          data: {
            bid: this._bid,
            userId: author.id,
            isUserBlocked: false,
            groupId: this._groupId,
          },
        })
        .afterClosed()
        .subscribe((result) => {
          if (result?.userBlocked) {
            this._eventBusService.fire('UserBlocked:refreshMemoLists');
          }
        });
    }
  }

  //이벤트를 통해 comment-right-panel 열기 요청
  _onClickMemoCommentButton(memo: Memo): void {
    this._eventBusService.fire('MemoListComponent:commentButtonClick', {
      memo: memo,
      source: this.isSmallPopup ? 'reading-mode-small-popup' : 'reading-mode', // 소스 정보 추가
    });
  }

  // textarea 내에서 화살표 키 사용 시 뷰어 페이지 이동 방지
  public _onTextareaArrowKey(event: any): void {
    event.stopPropagation();
  }

  // textarea 스크롤 이벤트 핸들러
  private _textareaScrollTimeouts: Map<HTMLTextAreaElement, NodeJS.Timeout> =
    new Map();

  public _onTextareaScroll(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;

    // 스크롤 중일 때 클래스 추가
    textarea.classList.add('is-scrolling');

    // 기존 타이머가 있으면 제거
    const existingTimeout = this._textareaScrollTimeouts.get(textarea);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // 스크롤이 멈춘 후 300ms 후에 클래스 제거
    const timeout = setTimeout(() => {
      textarea.classList.remove('is-scrolling');
      this._textareaScrollTimeouts.delete(textarea);
    }, 300);

    this._textareaScrollTimeouts.set(textarea, timeout);
  }

  // 답글 입력: Command/Ctrl
  public _onReplyTextareaKeyDown(
    event: KeyboardEvent,
    isMine: boolean,
    authorId: string
  ): void {
    // ESC 키 처리: 입력 취소
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this._onCancelReplyCommentInput();
      return;
    }

    // Command + Enter (Mac) 또는 Ctrl + Enter (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this._onSaveReplyCommentInput(isMine, authorId);
    }
  }

  // 답글의 답글 입력: Command/Ctrl
  public _onSubReplyTextareaKeyDown(
    event: KeyboardEvent,
    commentId: string,
    isMine: boolean,
    authorId: string
  ): void {
    // ESC 키 처리: 입력 취소
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this._onCloseSubReplyComment(commentId);
      return;
    }

    // Command + Enter (Mac) 또는 Ctrl + Enter (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this._onSaveSubReplyCommentInput(commentId, isMine, authorId);
    }
  }

  // 답글의 답글의 답글 입력: Command/Ctrl
  public _onSubSubReplyTextareaKeyDown(
    event: KeyboardEvent,
    commentId: string,
    replyId: string,
    isMine: boolean,
    authorId: string
  ): void {
    // ESC 키 처리: 입력 취소
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this._onCloseSubSubReplyComment(commentId, replyId);
      return;
    }

    // Command + Enter (Mac) 또는 Ctrl + Enter (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this._onSaveSubSubReplyCommentInput(commentId, replyId, isMine, authorId);
    }
  }

  // 답글 수정: Command/Ctrl
  public _onEditReplyTextareaKeyDown(
    event: KeyboardEvent,
    replyData: Comment,
    isMine: boolean,
    authorId: string
  ): void {
    // ESC 키 처리: 입력 취소
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this._onCloseReplyChangeButtonClick(replyData);
      return;
    }

    // Command + Enter (Mac) 또는 Ctrl + Enter (Windows/Linux)
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      this._onEditReplyCommentInput(replyData, isMine, authorId);
    }
  }

  _onReplyInputChange(): void {
    if (this.replyInput?.nativeElement instanceof HTMLTextAreaElement) {
      const textarea = this.replyInput.nativeElement;
      // 높이를 내용에 맞게 자동 조절
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      // 내용이 비어있거나 한 줄일 때는 22px, 그 이상일 때만 scrollHeight 사용
      const newHeight = scrollHeight <= 22 ? 22 : Math.min(scrollHeight, 200);
      textarea.style.height = `${newHeight}px`;
    }
  }

  getNicknameIndent(): string {
    if (this.nicknameElement?.nativeElement) {
      // 실제 DOM 요소의 너비를 측정
      const width = this.nicknameElement.nativeElement.offsetWidth + 5;
      return `${width}px`;
    }
    return '0px';
  }

  // 답글달기 클릭 이벤트
  _replyComment(): void {
    this._isReplyCommentListEmpty = false;

    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    // 화면 너비가 1200px보다 작은 경우 모바일 입력 폼 사용
    const isSmallScreen = window.innerWidth < 1200;

    if (isSmallScreen) {
      // 작은 화면에서는 부모 컴포넌트로 이벤트 전달하고 답글 입력창은 열지 않음
      this.replyCommentClick.emit({
        memo: this.memo,
        comment: this.memo,
        commentState: 'comment',
      });
      return;
    }

    // 큰 화면에서는 답글 입력창 열기
    this._isReplyCommentInputOpen = true;

    setTimeout(() => {
      if (this.replyInput) {
        this.replyInput.nativeElement.focus();
      }
    }, 0);
  }

  // 답글의 답글달기 클릭 이벤트
  _onSubReplyComment(commentId: string): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    const comment = this._replyComments.find(
      (comment) => comment.id === commentId
    );

    // 화면 너비가 1200px보다 작은 경우 모바일 입력 폼 사용
    const isSmallScreen = window.innerWidth < 1200;

    if (isSmallScreen) {
      // 작은 화면에서는 부모 컴포넌트로 이벤트 전달하고 답글 입력창은 열지 않음
      this.replyCommentClick.emit({
        memo: this.memo,
        comment: comment,
        commentState: 'subComment',
      });
      return;
    }

    if (comment) {
      comment.isSubReplyOpened = 1;
    }

    setTimeout(() => {
      // 클릭된 commentId에 해당하는 textarea를 직접 찾기
      const textarea = document.querySelector(
        `textarea[data-comment-id="${commentId}"]`
      ) as HTMLTextAreaElement;

      if (textarea) {
        textarea.focus();
      }
    }, 0);
  }

  // 답글의 답글의 답글달기 클릭 이벤트
  _onSubSubReplyComment(commentId: string, replyId: string): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    const comment = this._replyComments.find(
      (comment) => comment.id === commentId
    );

    const reply = comment?.replies.find((reply: any) => reply.id === replyId);

    // 화면 너비가 1200px보다 작은 경우 모바일 입력 폼 사용
    const isSmallScreen = window.innerWidth < 1200;

    if (isSmallScreen) {
      // 작은 화면에서는 부모 컴포넌트로 이벤트 전달하고 답글 입력창은 열지 않음
      this.replyCommentClick.emit({
        memo: this.memo,
        comment: comment,
        commentState: 'subSubComment',
        reply: reply,
      });
      return;
    }

    if (reply) {
      reply.isSubReplyOpened = 1;
    }

    setTimeout(() => {
      // 클릭된 replyId에 해당하는 textarea를 직접 찾기
      const textarea = document.querySelector(
        `textarea[data-comment-id="${replyId}"]`
      ) as HTMLTextAreaElement;

      if (textarea) {
        textarea.focus();
      }
    }, 0);
  }

  // 답글달기 닫기 이벤트
  _onCancelReplyCommentInput(): void {
    this._replyCommentInputValue = '';
    this._isReplyCommentInputOpen = false;
  }

  // 답글의 답글달기 닫기 이벤트
  _onCloseSubReplyComment(commentId: string): void {
    this._subReplyCommentInputValues[commentId] = '';
    const comment = this._replyComments.find(
      (comment) => comment.id === commentId
    );

    if (comment) {
      comment.isSubReplyOpened = 0;
    }
  }

  // 답글의 답글의 답글달기 닫기 이벤트
  _onCloseSubSubReplyComment(commentId: string, replyId: string): void {
    this._subSubReplyCommentInputValues[replyId] = '';
    const comment = this._replyComments.find(
      (comment) => comment.id === commentId
    );
    const reply = comment?.replies.find((reply: any) => reply.id === replyId);

    if (reply) {
      reply.isSubReplyOpened = 0;
    }
  }

  // 답글의 답글달기 입력값 변경 이벤트
  _onSubReplyInputChange(commentId: string, event: any): void {
    // 입력값 업데이트
    this._subReplyCommentInputValues[commentId] = event.target.value;

    this._adjustTextareaHeight(event.target);
  }

  // 답글의 답글의 답글 입력값 변경 이벤트
  _onSubSubReplyInputChange(replyId: string, event: any): void {
    this._subSubReplyCommentInputValues[replyId] = event.target.value;

    this._adjustTextareaHeight(event.target);
  }

  // textarea 높이 자동 조절
  private _adjustTextareaHeight(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const newHeight = scrollHeight <= 22 ? 22 : Math.min(scrollHeight, 200);
    textarea.style.height = `${newHeight}px`;
  }

  // 답글달기 저장 이벤트
  _onSaveReplyCommentInput(isMine: boolean, authorId: string): void {
    // 이미 답글 생성 중이면 무시
    if (this._isCreatingMemo) {
      return;
    }

    if (this._replyCommentInputValue.trim() === '') {
      return;
    }

    // 답글 생성 시작
    this._isCreatingMemo = true;

    this._commentApiService
      .createComment(
        this.memo.id,
        this._replyCommentInputValue.trim(),
        isMine ? '' : authorId
      )
      .subscribe({
        next: (result) => {
          this._replyCommentInputValue = '';
          this.memo.comment_count++;

          // 답글 더보기가 닫혀있으면 열기
          if (!this._isReplyCommentListOpen) {
            // 답글 목록이 아직 로드되지 않았다면 로드
            if (!this._isRequestReplyComment) {
              this._isRequestReplyComment = true;
              this._isReplyCommentListOpen = true;

              this._commentApiService
                .getComments(this.memo.id)
                .subscribe((replyResult) => {
                  if (replyResult) {
                    this._replyComments = replyResult.items.map((item) => {
                      item.isSubReplyOpened = 0;
                      item.isSubReplyChangeOpened = 0;
                      // 답글의 짧은 내용 계산
                      this._replyCommentShortContents[item.id] =
                        this._calculateShortContent(item.content);

                      item.replies = item.replies.map((reply) => {
                        reply.isSubReplyOpened = 0;
                        reply.isSubReplyChangeOpened = 0;
                        // 다답글의 짧은 내용 계산
                        this._subReplyShortContents[reply.id] =
                          this._calculateShortContent(reply.content);

                        return reply;
                      });

                      return item;
                    });
                  }
                  this._isReplyCommentInputOpen = false;
                });
            } else {
              // 이미 로드된 답글 목록이 있다면 새 답글 추가
              result.isSubReplyOpened = 0;
              result.isSubReplyChangeOpened = 0;
              // 답글의 짧은 내용 계산
              this._replyCommentShortContents[result.id] =
                this._calculateShortContent(result.content);
              // 답글의 답글들도 처리
              if (result.replies) {
                result.replies = result.replies.map((reply: any) => {
                  reply.isSubReplyOpened = 0;
                  reply.isSubReplyChangeOpened = 0;
                  // 다답글의 짧은 내용 계산
                  this._subReplyShortContents[reply.id] =
                    this._calculateShortContent(reply.content);
                  return reply;
                });
              }
              this._replyComments.push(result);
              this._isReplyCommentListOpen = true;
              this._isReplyCommentInputOpen = false;
            }
          } else {
            // 답글 더보기가 이미 열려있으면 새 답글 추가
            if (this._replyComments.length > 0) {
              this._replyComments.push(result);
              this._isReplyCommentInputOpen = false;
            }
          }
        },
        error: (error) => {
          this._isCreatingMemo = false;
          if (error.status === 404) {
            this._toastService.openWarning('존재하지 않는 댓글입니다.');
          }
        },
        complete: () => {
          this._isCreatingMemo = false;
        },
      });
  }

  // 답글의 답글 저장 이벤트
  _onSaveSubReplyCommentInput(
    commentId: string,
    isMine: boolean,
    authorId: string
  ): void {
    if (this._isCreatingMemo) {
      return;
    }

    const inputValue =
      this._subReplyCommentInputValues[commentId]?.trim() || '';

    if (inputValue === '') {
      return;
    }

    // 답글의 답글 생성 시작
    this._isCreatingMemo = true;

    this._commentApiService
      .createReply(this.memo.id, inputValue, isMine ? '' : authorId, commentId) // parentId로 commentId 전달
      .subscribe({
        next: (result) => {
          this._subReplyCommentInputValues[commentId] = '';
          this._eventBusService.fire(
            'ReadingGroupMemoListComponent:subReplyCommentAdded',
            {
              memoId: this.memo.id,
              commentId: commentId,
              reply: {
                ...result,
                isSubReplyOpened: 0,
              },
            }
          );
        },
        error: (error) => {
          this._isCreatingMemo = false;
          if (error.status === 404) {
            this._toastService.openWarning('존재하지 않는 댓글입니다.');
          }
        },
        complete: () => {
          this._isCreatingMemo = false;
        },
      });
  }

  // 답글의 답글의 답글 저장 이벤트
  _onSaveSubSubReplyCommentInput(
    commentId: string,
    replyId: string,
    isMine: boolean,
    authorId: string
  ): void {
    // 이미 답글 생성 중이면 무시
    if (this._isCreatingMemo) {
      return;
    }

    const inputValue =
      this._subSubReplyCommentInputValues[replyId]?.trim() || '';

    if (inputValue === '') {
      return;
    }

    // 답글의 답글의 답글 생성 시작
    this._isCreatingMemo = true;

    this._commentApiService
      .createReply(this.memo.id, inputValue, isMine ? '' : authorId, commentId) // parentId로 replyId 전달
      .subscribe({
        next: (result) => {
          // reading-group-memo-list 컴포넌트에 답글의 답글의 답글 전달
          this._eventBusService.fire(
            'ReadingGroupMemoListComponent:subSubReplyCommentAdded',
            {
              memoId: this.memo.id,
              commentId: commentId,
              replyId: replyId,
              reply: {
                ...result,
                isSubReplyOpened: 0,
              },
            }
          );
        },
        error: (error) => {
          this._isCreatingMemo = false;
          if (error.status === 404) {
            this._toastService.openWarning('존재하지 않는 댓글입니다.');
          }
        },
        complete: () => {
          this._isCreatingMemo = false;
        },
      });
  }

  // 현재 메모로 스크롤 이동하는 메서드 수정
  private _scrollToThisMemo(): void {
    const existingFocusedMemos = document.querySelectorAll(
      'viewer-memo.focused-memo'
    );
    existingFocusedMemos.forEach((memo) => {
      memo.classList.remove('focused-memo');
    });

    // 현재 컴포넌트의 DOM 요소 찾기
    const memoElement = document.querySelector(
      `viewer-memo[data-memo-id="${this.memo.id}"]`
    );

    if (memoElement) {
      // 스크롤 가능한 부모 컨테이너 찾기 (.body 클래스)
      const scrollContainer = memoElement.closest('.body');

      if (scrollContainer) {
        // 컨테이너와 메모 요소의 위치 계산
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = memoElement.getBoundingClientRect();

        // 컨테이너의 현재 스크롤 위치
        const currentScrollTop = scrollContainer.scrollTop;

        // 메모를 컨테이너 중앙에 위치시키기 위한 스크롤 계산
        const elementOffsetFromContainer = elementRect.top - containerRect.top;
        const targetScrollTop =
          currentScrollTop +
          elementOffsetFromContainer -
          containerRect.height / 2 +
          elementRect.height / 2;

        // 부드러운 스크롤로 이동
        scrollContainer.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth',
        });
      } else {
        // 컨테이너를 찾지 못한 경우 기본 동작
        memoElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }

      // 포커스 효과를 위한 클래스 추가
      memoElement.classList.add('focused-memo');
    }
  }

  // 해당 댓글에 포함된 답글 요청
  onReplyCommentOpened(): void {
    this._isReplyCommentListOpen = !this._isReplyCommentListOpen;

    if (this._isRequestReplyComment) {
      return;
    }

    this._isRequestReplyComment = true;

    this._commentApiService.getComments(this.memo.id).subscribe((result) => {
      if (result) {
        this._replyComments = result.items.map((item) => {
          item.isSubReplyOpened = 0;
          item.isSubReplyChangeOpened = 0;
          // 답글의 짧은 내용 계산
          this._replyCommentShortContents[item.id] =
            this._calculateShortContent(item.content);

          item.replies = item.replies.map((reply) => {
            reply.isSubReplyOpened = 0;
            reply.isSubReplyChangeOpened = 0;
            // 다답글의 짧은 내용 계산
            this._subReplyShortContents[reply.id] = this._calculateShortContent(
              reply.content
            );

            return reply;
          });

          return item;
        });
      }
    });
  }

  // 댓글 메뉴 오픈 이벤트
  _onCommentMenuOpen(comment: Comment): void {
    this._selectedCommentForMoreButton = comment;
  }

  // 답글 삭제 이벤트
  _onDeleteReplyButtonClick(replyData: Comment): void {
    const dialogRef = this._alertService.openConfirm(
      undefined,
      '답글을 삭제하시겠습니까?',
      '삭제하기'
    );

    dialogRef.afterClosed().subscribe((ok) => {
      if (ok) {
        this._commentApiService
          .deleteComment(this.memo.id, replyData.id)
          .subscribe(() => {
            this._replyComments = this._replyComments.filter(
              (comment) => comment.id !== replyData.id
            );
            this.memo.comment_count--;

            // 답글의 답글 목록 갯수만큼 빼기
            const replyCount = replyData.replies.length;
            this.memo.comment_count -= replyCount;

            // 답글 목록을 모두 지웠으면 빈 상태로 변경
            if (this.memo.comment_count === 0) {
              this._isReplyCommentListEmpty = true;
              this._isRequestReplyComment = false;
              this._isReplyCommentListOpen = false;
            }
          });
      }
    });
  }

  // 답글의 답글 삭제 이벤트
  _onDeleteSubReplyButtonClick(
    subReplyData: Comment,
    commentData: Comment
  ): void {
    const dialogRef = this._alertService.openConfirm(
      undefined,
      '답글을 삭제하시겠습니까?',
      '삭제하기'
    );

    dialogRef.afterClosed().subscribe((ok) => {
      if (ok) {
        this._commentApiService
          .deleteComment(this.memo.id, subReplyData.id)
          .subscribe(() => {
            const comment = this._replyComments.find(
              (comment) => comment.id === commentData.id
            );

            comment.replies = comment?.replies.filter(
              (reply: any) => reply.id !== subReplyData.id
            );

            this.memo.comment_count--;
          });
      }
    });
  }

  // 답글 수정 박스 닫기 이벤트
  _onCloseReplyChangeButtonClick(replyData: Comment): void {
    this._subReplyCommentInputValues[replyData.id] = '';
    replyData.isSubReplyChangeOpened = 0;
  }

  // 답글 수정 저장 버튼 활성화 여부 확인 (trim 비교)
  isReplyEditSaveEnabled(replyData: Comment): boolean {
    const originalContent = replyData.content?.trim() || '';
    const inputContent =
      this._subReplyCommentInputValues[replyData.id]?.trim() || '';
    return originalContent !== inputContent && inputContent !== '';
  }

  // 답글 수정 박스 오픈 이벤트
  _onEditReplyButtonClick(replyData: Comment): void {
    // 1200px 이하 화면에서는 답글 수정 박스 오픈 이벤트 발생 안함
    const isSmallScreen = window.innerWidth < 1200;

    if (isSmallScreen) {
      this.subReplyCommentChanged.emit({
        memo: this.memo,
        reply: replyData,
      });
      return;
    }

    replyData.isSubReplyChangeOpened = 1;
    // 위아래 공백 제거하여 입력값에 설정
    this._subReplyCommentInputValues[replyData.id] = replyData.content.trim();

    setTimeout(() => {
      // 클릭된 commentId에 해당하는 textarea를 직접 찾기
      const textarea = document.querySelector(
        `textarea[data-comment-id="${replyData.id}"]`
      ) as HTMLTextAreaElement;

      if (textarea) {
        // 기존 내용에 맞게 높이 조정
        this._adjustTextareaHeight(textarea);
        textarea.focus();
      }
    }, 0);
  }

  // 답글 수정
  _onEditReplyCommentInput(
    replyData: Comment,
    isMine: boolean,
    authorId: string
  ): void {
    // 이미 답글 수정 중이면 무시
    if (this._isCreatingMemo) {
      return;
    }

    // 위아래 공백 제거
    const editContent =
      this._subReplyCommentInputValues[replyData.id]?.trim() || '';

    if (editContent === '') {
      return;
    }

    if (editContent === replyData.content.trim()) {
      return;
    }

    // 답글 수정 시작
    this._isCreatingMemo = true;

    this._commentApiService
      .updateComment(
        this.memo.id,
        editContent,
        isMine ? '' : authorId,
        replyData.id
      )
      .subscribe({
        next: (result) => {
          replyData.content = result.content;
          replyData.updated_at = result.updated_at;
          replyData.isSubReplyChangeOpened = 0;
        },
        error: () => {
          this._isCreatingMemo = false;
        },
        complete: () => {
          this._isCreatingMemo = false;
        },
      });
  }

  // 답글 신고 이벤트
  _onReportCommentMenuClick(replyData: Comment): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    if (!this.memo) {
      return;
    }

    this._dialogService
      .open(CommentReportDialogComponent, {
        data: {
          authorName: replyData.author.nickname,
          authorAvatarImageURL: replyData.author.avatar_image_url,
          memoId: this.memo!.id,
          commentId: replyData.id,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result?.blocked) {
          this._eventBusService.fire('UserBlocked:refreshMemoLists');
        }
      });
  }

  // 답글 숨기기 이벤트
  _onBlockReplyMenuClick(replyData: Comment, isSubReply: boolean): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    this._alertService
      .openConfirm(
        undefined,
        '선택한 답글을 숨김 처리합니다. 숨김 처리 후에는 다시 볼 수 없습니다. 계속하시겠습니까?',
        '계속하기'
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (!this.memo) {
            return;
          }

          this._commentApiService
            .blockComment(this.memo.id, replyData.id)
            .subscribe(() => {
              if (isSubReply) {
                const comment = this._replyComments.find(
                  (comment) => comment.id === replyData.comment_id
                );
                if (comment) {
                  comment.replies = comment.replies.filter(
                    (reply: any) => reply.id !== replyData.id
                  );

                  this.memo.comment_count--;
                }
              } else {
                this._replyComments = this._replyComments.filter(
                  (comment) => comment.id !== replyData.id
                );

                this.memo.comment_count--;
                this.memo.comment_count -= replyData.replies.length;
              }
            });
        }
      });
  }

  // 답글 추천 이벤트
  _onLikeReplyButtonClick(commentId: string): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    if (!this.memo) {
      return;
    }

    const targetComment = this._replyComments.find(
      (comment) => comment.id === commentId
    );
    if (!targetComment) {
      return;
    }

    const key = `reply:${commentId}`;
    if (!this._replyLikeBaseline.has(key)) {
      this._replyLikeBaseline.set(key, {
        like_count: targetComment.like_count,
        is_liked: targetComment.is_liked,
      });
    }

    const wasLiked = targetComment.is_liked === 1;
    targetComment.is_liked = wasLiked ? 0 : 1;
    targetComment.like_count += wasLiked ? -1 : 1;

    this._scheduleReplyLikeDebounced(key, () =>
      this._flushReplyLikeRequest(commentId, key)
    );
  }

  // 답글의 답글 추천 이벤트
  _onLikeSubReplyButtonClick(commentId: string, replyId: string): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    if (!this.memo) {
      return;
    }

    const targetComment = this._replyComments.find(
      (comment) => comment.id === commentId
    );
    const targetReply = targetComment?.replies?.find(
      (reply: { id: string }) => reply.id === replyId
    );
    if (!targetReply) {
      return;
    }

    const key = `sub:${commentId}:${replyId}`;
    if (!this._replyLikeBaseline.has(key)) {
      this._replyLikeBaseline.set(key, {
        like_count: targetReply.like_count,
        is_liked: targetReply.is_liked,
      });
    }

    const wasLiked = targetReply.is_liked === 1;
    targetReply.is_liked = wasLiked ? 0 : 1;
    targetReply.like_count += wasLiked ? -1 : 1;

    this._scheduleReplyLikeDebounced(key, () =>
      this._flushSubReplyLikeRequest(commentId, replyId, key)
    );
  }

  private _resetMemoLikePendingState(): void {
    if (this._memoLikeDebounceHandle !== null) {
      clearTimeout(this._memoLikeDebounceHandle);
      this._memoLikeDebounceHandle = null;
    }
    this._memoLikeBaseline = null;
    this._memoLikeIntended = null;
  }

  private _scheduleMemoLikeDebounce(): void {
    if (this._memoLikeDebounceHandle !== null) {
      clearTimeout(this._memoLikeDebounceHandle);
    }
    this._memoLikeDebounceHandle = setTimeout(() => {
      this._memoLikeDebounceHandle = null;
      this._flushMemoLikeRequest();
    }, ReadingModeMemoComponent._likeDebounceMs);
  }

  private _flushMemoLikeRequest(): void {
    const base = this._memoLikeBaseline;
    const int = this._memoLikeIntended;
    if (base === null || int === null) {
      this._resetMemoLikePendingState();
      return;
    }

    if (int.is_liked === base.is_liked && int.like_count === base.like_count) {
      this._resetMemoLikePendingState();
      return;
    }

    const revertSnapshot = { ...base };
    const intendedSnapshot = { ...int };
    this._resetMemoLikePendingState();

    const request$ =
      intendedSnapshot.is_liked === 1
        ? this._annotationsService.likeMemo(this.memo.id)
        : this._annotationsService.unlikeMemo(this.memo.id);

    request$.subscribe({
      error: (err: unknown) => {
        this.memoUpdated.emit(revertSnapshot);
        const msg = err instanceof APIError ? err.message : undefined;
        this._toastService.openWarning(
          msg ?? '추천 반영에 실패했습니다. 다시 시도해 주세요.'
        );
      },
    });
  }

  private _scheduleReplyLikeDebounced(key: string, flush: () => void): void {
    const existing = this._replyLikeDebounceHandles.get(key);
    if (existing !== undefined) {
      clearTimeout(existing);
    }
    const handle = setTimeout(() => {
      this._replyLikeDebounceHandles.delete(key);
      flush();
    }, ReadingModeMemoComponent._likeDebounceMs);
    this._replyLikeDebounceHandles.set(key, handle);
  }

  private _likeFailureToastMessage(err: unknown): string {
    return err instanceof APIError
      ? err.message
      : '추천 반영에 실패했습니다. 다시 시도해 주세요.';
  }

  private _flushReplyLikeRequest(commentId: string, key: string): void {
    if (!this.memo) {
      this._replyLikeBaseline.delete(key);
      return;
    }

    const target = this._replyComments.find((c) => c.id === commentId);
    const base = this._replyLikeBaseline.get(key);
    if (!target || base === undefined) {
      this._replyLikeBaseline.delete(key);
      return;
    }

    const intended = {
      like_count: target.like_count,
      is_liked: target.is_liked,
    };
    if (
      intended.is_liked === base.is_liked &&
      intended.like_count === base.like_count
    ) {
      this._replyLikeBaseline.delete(key);
      return;
    }

    const revertSnapshot = { ...base };
    this._replyLikeBaseline.delete(key);

    const request$ =
      intended.is_liked === 1
        ? this._commentApiService.recommendComment(this.memo.id, commentId)
        : this._commentApiService.unRecommendComment(this.memo.id, commentId);

    request$.subscribe({
      error: (err: unknown) => {
        target.like_count = revertSnapshot.like_count;
        target.is_liked = revertSnapshot.is_liked;
        this._toastService.openWarning(this._likeFailureToastMessage(err));
      },
    });
  }

  private _flushSubReplyLikeRequest(
    commentId: string,
    replyId: string,
    key: string
  ): void {
    if (!this.memo) {
      this._replyLikeBaseline.delete(key);
      return;
    }

    const targetComment = this._replyComments.find((c) => c.id === commentId);
    const target = targetComment?.replies?.find(
      (reply: { id: string }) => reply.id === replyId
    );
    const base = this._replyLikeBaseline.get(key);
    if (!target || base === undefined) {
      this._replyLikeBaseline.delete(key);
      return;
    }

    const intended = {
      like_count: target.like_count,
      is_liked: target.is_liked,
    };
    if (
      intended.is_liked === base.is_liked &&
      intended.like_count === base.like_count
    ) {
      this._replyLikeBaseline.delete(key);
      return;
    }

    const revertSnapshot = { ...base };
    this._replyLikeBaseline.delete(key);

    const request$ =
      intended.is_liked === 1
        ? this._commentApiService.recommendComment(this.memo.id, replyId)
        : this._commentApiService.unRecommendComment(this.memo.id, replyId);

    request$.subscribe({
      error: (err: unknown) => {
        target.like_count = revertSnapshot.like_count;
        target.is_liked = revertSnapshot.is_liked;
        this._toastService.openWarning(this._likeFailureToastMessage(err));
      },
    });
  }

  // 공유하기 이벤트
  _onShareButtonClick(commentId: string): void {
    if (!this.isOwnedByUser) {
      this._onWarningPurchaseBook();
      return;
    }

    // 대시보드에서 공유가 불가능한 비공개 페이지로 설정한 부분인경우
    if (!this._canShareItem) {
      this._toastService.openWarning(
        '저작권자의 요청으로 본 댓글은 공유가 제한됩니다.'
      );
      return;
    }

    const url = window.location.origin;
    const pathWithQuery = window.location.pathname + window.location.search;
    navigator.clipboard.writeText(
      url + pathWithQuery + `&commentId=${commentId}`
    );

    this._toastService.open('댓글 공유 링크가 복사되었습니다.');
  }

  _onWarningPurchaseBook(): void {
    this._toastService
      .openWarning('책 구매 후 이용 가능합니다.', '책 구매하러 가기.')
      .onAction()
      .subscribe((result: string) => {
        if (result === '책 구매하러 가기.') {
          window.open(`${location.origin}/@${this._bid}`, '_blank');
        }
      });
  }

  // 답글의 짧은 내용 계산
  private _calculateShortContent(content: string): string | undefined {
    let lines = content.split('\n');

    if (lines.length > 3) {
      lines = lines.slice(0, 3);
      return lines.join('\n').slice(0, 64);
    } else if (content.length > 64) {
      return content.slice(0, 64);
    } else {
      return undefined;
    }
  }

  // 답글의 짧은 내용 가져오기
  getReplyCommentShortContent(
    commentId: string,
    content: string
  ): string | undefined {
    // 명시적으로 설정되지 않은 경우에만 계산
    if (!(commentId in this._replyCommentShortContents)) {
      this._replyCommentShortContents[commentId] =
        this._calculateShortContent(content);
    }
    return this._replyCommentShortContents[commentId];
  }

  // 다답글의 짧은 내용 가져오기
  getSubReplyShortContent(
    replyId: string,
    content: string
  ): string | undefined {
    // 명시적으로 설정되지 않은 경우에만 계산
    if (!(replyId in this._subReplyShortContents)) {
      this._subReplyShortContents[replyId] =
        this._calculateShortContent(content);
    }
    return this._subReplyShortContents[replyId];
  }

  // 답글 자세히보기 클릭
  onReplyCommentShowAll(commentId: string): void {
    this._replyCommentShortContents[commentId] = undefined;
  }

  // 답글 간략히 클릭
  onReplyCommentShowLess(commentId: string, content: string): void {
    this._replyCommentShortContents[commentId] =
      this._calculateShortContent(content);
  }

  // 다답글 자세히보기 클릭
  onSubReplyShowAll(replyId: string): void {
    this._subReplyShortContents[replyId] = undefined;
  }

  // 다답글 간략히 클릭
  onSubReplyShowLess(replyId: string, content: string): void {
    this._subReplyShortContents[replyId] = this._calculateShortContent(content);
  }

  // 링크 클릭 처리 로직 (답글과 다답글 포함)
  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const target = event.target as HTMLAnchorElement;

    if (target.tagName === 'A' && target.href) {
      // 하이퍼링크 클릭시 이벤트 버블링 방지
      event.stopPropagation();

      // memo url과 memo에 bid 추출
      const memoUrl = target.href;
      const regex = /(https?:\/\/[^/:]+(?::\d+)?)\/?(@[^/]+)?/;
      const match = memoUrl.match(regex);
      const domainRegex = /http[s]?:\/\/(?<domain>[^/]+)/;
      const domainMatch = memoUrl.match(domainRegex);

      const origin = window.location.origin; // 현재 호스트 주소
      const urlBid = this._router.url.split('/')[1]; // 현재 URL에 bid 추출

      if (!match) {
        return;
      }

      if (!domainMatch) {
        return;
      }

      if (!domainMatch.groups) {
        return;
      }

      if (
        (origin === match[1] && urlBid === match[2]) ||
        (/(?:^|\.)buk\.io$/.test(domainMatch.groups['domain']) &&
          urlBid === match[2])
      ) {
        // https://buk.io 인 경우 새로운 창에서 메인 페이지로 이동하도록 분기처리
        if (memoUrl.replace(match[1], '') === '/') {
          return;
        }

        // 조건에 만족할경우 a 태그 기본 동작 방지 및 앵귤러 navigate로 새로고침 없이 이동하도록 수정
        event.preventDefault();

        this._eventBusService.fire('AnnotationsComponent:itemClick', {
          url: memoUrl.replace(match[1], ''),
        });
      }
    }

    return;
  }
}
